import { useState } from 'react';
import { Skeleton } from '@mui/material';
import { Link } from 'react-router';
import { useGetAdminBlocksQuery } from 'store/services/admin/adminApi';
import { AdminBlockEntry } from 'types/interfaces/admin/blocks';
import MagicButton from 'components/MagicButton/MagicButton';
import { adminUserDetailPath } from 'routes/paths';
import { formatAdminDate } from 'utils/formatAdminDate';
import './BlocksTab.css';

const PAGE_SIZE = 50;

const BlocksTab = () => {
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useGetAdminBlocksQuery({
    page,
    pageSize: PAGE_SIZE,
  });

  const getErrorMessage = (err: any): string => {
    const status = err?.status || err?.originalStatus;

    if (status === 401 || status === 403) return 'You do not have permission to view this resource';
    if (status === 404) return 'Block data is temporarily unavailable';
    if (status === 500) return 'Server error. Our team has been notified. Please try again in a few minutes.';
    if (status === 'FETCH_ERROR') return 'Network error. Please check your internet connection.';
    if (status === 'TIMEOUT_ERROR') return 'Request timed out. Please try again.';

    return 'Unable to load blocks. Please try again.';
  };

  const shouldShowRetry = (err: any): boolean => {
    const status = err?.status || err?.originalStatus;
    return status !== 401 && status !== 403;
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && page < data.pagination.totalPages) {
      setPage(page + 1);
    }
  };

  const tableHeader = (
    <div className="admin-blocks-table-header">
      <div className="admin-blocks-col-blocker">Blocker</div>
      <div className="admin-blocks-col-blocked">Blocked</div>
      <div className="admin-blocks-col-date">Created At</div>
      <div className="admin-blocks-col-reason">Reason</div>
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="admin-blocks-tab">
        <div className="admin-blocks-content">
          <div className="admin-blocks-table">
            {tableHeader}
            <div className="admin-blocks-loading">
              {[...Array(10)].map((_, index) => (
                <div key={index} className="admin-blocks-row-skeleton">
                  <Skeleton variant="text" width={150} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Skeleton variant="text" width={150} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="admin-blocks-tab">
        <div className="admin-blocks-content">
          <div className="admin-blocks-error-state">
            <div className="admin-blocks-error-icon">⚠️</div>
            <h2 className="admin-blocks-error-title">Unable to Load Blocks</h2>
            <p className="admin-blocks-error-message">{getErrorMessage(error)}</p>
            {shouldShowRetry(error) && (
              <MagicButton
                label="Try Again"
                color="purple"
                onPress={() => refetch()}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (data && data.blocks.length === 0) {
    return (
      <div className="admin-blocks-tab">
        <div className="admin-blocks-content">
          <div className="admin-blocks-empty-state">
            <h2 className="admin-blocks-empty-title">No Blocks Yet</h2>
            <p className="admin-blocks-empty-description">
              No user has blocked another user.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state with data
  return (
    <div className="admin-blocks-tab">
      <div className="admin-blocks-content">
        <div className="admin-blocks-table">
          {tableHeader}

          <div className="admin-blocks-table-body">
            {data?.blocks.map((block: AdminBlockEntry) => (
              <div key={`${block.blockerId}-${block.blockedId}`} className="admin-blocks-row">
                <div className="admin-blocks-col-blocker">
                  <Link to={adminUserDetailPath(block.blockerId)} className="admin-blocks-user-link">
                    {block.blockerUsername}
                  </Link>
                </div>
                <div className="admin-blocks-col-blocked">
                  <Link to={adminUserDetailPath(block.blockedId)} className="admin-blocks-user-link">
                    {block.blockedUsername}
                  </Link>
                </div>
                <div className="admin-blocks-col-date">
                  {formatAdminDate(block.createdAt)}
                </div>
                <div className="admin-blocks-col-reason">
                  {block.reason?.trim() ? block.reason : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {data && data.pagination.totalPages > 1 && (
          <div className="admin-blocks-pagination">
            <MagicButton
              label="Previous"
              color="grey"
              onPress={handlePreviousPage}
              isDisabled={page === 1}
            />

            <span className="admin-blocks-pagination-info">
              Page {data.pagination.currentPage} of {data.pagination.totalPages}
              {' '}({data.pagination.totalBlocks} total blocks)
            </span>

            <MagicButton
              label="Next"
              color="grey"
              onPress={handleNextPage}
              isDisabled={page >= data.pagination.totalPages}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BlocksTab;
